import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Check, Users, ArrowRight } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Props extends Omit<AppShellProps, 'children'> {
  event: { id: number; name: string }
  urls: AppShellProps['urls'] & {
    newOrder1: string
    newOrder2: string
    newOrder3: string
    newOrder4: string
  }
  translations: AppShellProps['translations'] & {
    trialVersion: string
    pleaseUpgrade: string
    upgradeNow: string
    platformFees: string
    landingPage: string
    upTo: string
    registration: string
    autoBadge: string
    autoQr: string
    attendanceReports: string
    additionalOnsiteServices: string
    printing: string
    perBadge: string
    serviceFee: string
    perDay: string
    registrationDesk: string
    unlimited: string
    negotiable: string
  }
}

/* ------------------------------------------------------------------ */
/*  Tier data                                                          */
/* ------------------------------------------------------------------ */

const tiers = [
  {
    range: '1-100',
    platform: 500,
    limit: 100,
    printing: 14,
    dailyCost: 1000,
    desks: '1',
    bg: '#2b62a4',
    urlKey: 'newOrder1' as const,
  },
  {
    range: '100-500',
    platform: 1750,
    limit: 500,
    printing: 12,
    dailyCost: 1300,
    desks: '3',
    bg: '#235086',
    urlKey: 'newOrder2' as const,
  },
  {
    range: '500-1000',
    platform: 2700,
    limit: 1000,
    printing: 10,
    dailyCost: 1750,
    desks: '6',
    bg: '#1d4473',
    urlKey: 'newOrder3' as const,
  },
  {
    range: 'Unlimited',
    platform: 4500,
    limit: null,
    printing: 8,
    dailyCost: null,
    desks: '10',
    bg: '#252854',
    urlKey: 'newOrder4' as const,
  },
]

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Prepare(props: Props) {
  const { event, urls, translations: t, ...shell } = props

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={t.pleaseUpgrade} />

      <div className="space-y-8">
        {/* Trial notice */}
        <div className="rounded-xl border border-[#E8E8E5] bg-white p-6">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <div>
              <h1 className="text-2xl font-bold text-[#37352F]">{t.trialVersion}</h1>
              <p className="mt-1 text-lg text-[#91918E]">{t.pleaseUpgrade}</p>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-[#F1F1EF] px-6 py-4">
              <Users className="h-8 w-8 text-[#2383E2]" />
              <div className="text-center">
                <p className="text-sm font-medium text-[#91918E]">{t.registration}</p>
                <p className="text-2xl font-bold text-[#37352F]">0 / 5</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing tiers */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
          {tiers.map((tier) => (
            <Card
              key={tier.range}
              className="relative overflow-hidden border-0 text-white shadow-lg"
              style={{ backgroundColor: tier.bg }}
            >
              {/* Range badge */}
              <div className="-mt-0 mx-4 mb-3 rounded-lg bg-white px-4 py-3 text-center shadow-md" style={{ marginTop: '-1px' }}>
                <span className="text-2xl font-bold text-[#37352F]">{tier.range}</span>
              </div>

              <CardContent className="space-y-4 px-5 pb-5 pt-0">
                <h3 className="text-center text-base font-medium text-white/90">
                  {t.platformFees} {tier.platform} SAR
                </h3>

                <ul className="space-y-1.5 text-sm text-white/80">
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {t.landingPage}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {tier.limit
                      ? `${t.upTo} ${tier.limit} ${t.registration}`
                      : t.unlimited}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {t.autoBadge}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {t.autoQr}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {t.attendanceReports}
                  </li>
                </ul>

                <div>
                  <h4 className="mb-2 text-center text-sm font-medium text-white/90">
                    {t.additionalOnsiteServices}
                  </h4>
                  <ul className="space-y-1.5 text-sm text-white/80">
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {t.printing} {tier.printing} SAR {t.perBadge}
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {t.serviceFee} {tier.dailyCost ? `${tier.dailyCost} ${t.perDay}` : t.negotiable}
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {t.upTo} {tier.desks} {t.registrationDesk}
                    </li>
                  </ul>
                </div>

                <div className="pt-2 text-center">
                  <Button
                    variant="secondary"
                    onClick={() => router.visit(urls[tier.urlKey])}
                    className="bg-white px-6 font-semibold text-[#37352F] hover:bg-gray-100"
                  >
                    {t.upgradeNow}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </AppShell>
  )
}
